'use client'
import React, { useImperativeHandle, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Title from '../Title/index.js'
import { cn, getLocalizedPath } from '../../helpers/utils.js'
import Picture from '../../components/picture.js'
import SwiperBox from '../SwiperBox/index.js'
import { withLayout } from '../../shared/Styles.js'
import { Avatar, AvatarImage, AvatarFallback } from '../../components/avatar.js'
import { Container } from '../../components/container.js'
import { Text } from '../../components/text.js'
import type { ContainerProps, Theme } from '../../types/props.js'
import { useExposure } from '../../hooks/useExposure.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import type { Img } from '../../types/props.js'

const componentType = 'copy'
const componentName = 'product_review'

export interface EvaluateItem {
  /** 产品图片*/
  img: Img
  title: string
  subTitle: string
  description: string
  avatar?: {
    url: string
  }
  rating: number
  link?: string
}

export interface EvaluateProps extends React.HTMLAttributes<HTMLDivElement> {
  data: {
    title?: string
    products: EvaluateItem[]
    /** 形状 */
    shape?: 'round' | 'square'
    /** 主题色*/
    theme?: Theme
    containerProps?: ContainerProps
  }
  /** 按钮事件*/
  event?: {
    primaryButton?: (_v: any, _index: number) => void
  }
  key?: string
}

const EvaluateItem = ({ data, configuration }: { data: EvaluateItem; configuration?: any }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; text: string }>({
    visible: false,
    x: 0,
    y: 0,
    text: '',
  })

  const handleRating = (num: number) => {
    const raw = Number(num)
    const count = Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0
    return Array.from({ length: count }) || []
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (data?.description) {
      setTooltip({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        text: data.description,
      })
    }
  }

  const handleMouseLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }))
  }

  useExposure(ref, {
    componentType,
    componentName,
    componentTitle: data?.title,
    componentDescription: data?.description,
    position: configuration?.index + 1,
  })

  return (
    <div
      ref={ref}
      className={cn(
        'bg-background tablet:hover:bg-info-white box-border w-full overflow-hidden duration-300',
        configuration?.shape === 'round' ? 'rounded-2xl' : 'rounded-none',
        'lg-desktop:aspect-w-[404] lg-desktop:aspect-h-[480] desktop:aspect-w-[316] desktop:aspect-h-[384]',
        'laptop:aspect-w-[288] laptop:aspect-h-[360] aspect-w-[296] aspect-h-[360] relative',
        'md-tablet:h-[360px]'
      )}
    >
      <div className="desktop:p-6 absolute inset-0 box-border flex flex-col overflow-hidden p-4">
        <div className={cn('lg-desktop:mb-16 desktop:mb-10 mb-8 flex w-full justify-between')}>
          <div className="flex-1">
            {data?.title ? <h4 className="laptop:text-lg text-sm font-bold">{data?.title}</h4> : null}
            <div className="mt-1 flex items-center">
              {handleRating(data?.rating)?.map?.((_, index) => {
                return (
                  <div key={index} className="mr-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M7.32745 1.36274C7.60256 0.805304 8.39744 0.805305 8.67255 1.36274L10.1766 4.41035C10.2859 4.63171 10.4971 4.78514 10.7413 4.82063L14.1046 5.30934C14.7197 5.39873 14.9654 6.15471 14.5202 6.58861L12.0866 8.96084C11.9098 9.13315 11.8292 9.3814 11.8709 9.62469L12.4454 12.9743C12.5505 13.587 11.9074 14.0542 11.3572 13.765L8.349 12.1835C8.13051 12.0686 7.86949 12.0686 7.65099 12.1835L4.64282 13.765C4.0926 14.0542 3.44953 13.587 3.55461 12.9743L4.12912 9.62469C4.17085 9.3814 4.09019 9.13315 3.91342 8.96084L1.47976 6.58861C1.03462 6.15471 1.28025 5.39873 1.89542 5.30934L5.25866 4.82063C5.50294 4.78514 5.71412 4.63171 5.82336 4.41035L7.32745 1.36274Z"
                        fill="#F77234"
                      />
                    </svg>
                  </div>
                )
              })}
            </div>
          </div>
          <div className="desktop:size-12 size-10 overflow-hidden">
            {data?.avatar?.url ? (
              <Avatar className="size-full" isAdaptation={!data?.avatar?.url}>
                <AvatarImage src={data?.avatar?.url} />
                <AvatarFallback>{data?.title}</AvatarFallback>
              </Avatar>
            ) : null}
          </div>
        </div>
        <div className="flex-1" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
          <Text
            className={cn(
              'lg-desktop:text-2xl text-info-primary evaluate-description line-clamp-5 break-words text-xl font-bold leading-[1.2]'
            )}
          >
            {data?.description || ''}
          </Text>
          {typeof window !== 'undefined' &&
            tooltip.visible &&
            createPortal(
              <div
                className="pointer-events-none fixed z-[9999] max-w-xs rounded-lg bg-black/60  px-3 py-2 text-sm text-white shadow-lg backdrop-blur-sm transition-opacity duration-200"
                style={{
                  left: `${tooltip.x + 16}px`,
                  top: `${tooltip.y + 16}px`,
                }}
              >
                {tooltip.text}
              </div>,
              document.body
            )}
        </div>
        <a
          href={trackUrlRef(
            getLocalizedPath(data?.link || '', configuration?.locale || 'us'),
            `${componentType}_${componentName}`
          )}
          onClick={() => {
            configuration?.event?.primaryButton(data, configuration?.index + 1)
          }}
          data-headless-type-name={`${componentType}#${componentName}`}
          data-headless-title-desc-button={`${data?.title}#${data?.description || data?.subTitle}`}
          data-headless-nav-position={`''#${configuration?.index + 1}`}
        >
          <div
            className={cn(
              'mt-3 flex cursor-pointer items-center overflow-hidden bg-[#F7F8F9] p-3',
              configuration?.shape === 'round' ? 'rounded-lg' : 'rounded-none'
            )}
          >
            <div className="laptop:size-16 desktop:size-20 mr-3 flex size-12 items-center">
              <Picture className="w-full object-cover" source={data?.img?.url} alt={data?.img?.alt || ''} />
            </div>
            <p className="lg-desktop:text-[18px] desktop-[16px] line-clamp-2 flex-1 text-[14px] font-bold tracking-[-0.04em]">
              {data?.subTitle || ''}
            </p>
          </div>
        </a>
      </div>
    </div>
  )
}

const Evaluate = React.forwardRef<HTMLDivElement, EvaluateProps>(({ className = '', data, key, ...rest }, ref) => {
  const { products, title, theme, ...other } = data
  const innerRef = useRef<HTMLDivElement>(null)
  const { locale = 'us' } = useAiuiContext()
  useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

  return (
    <div {...rest} ref={innerRef} className={className}>
      <div className="evaluate-box relative z-10">
        <Container {...(data?.containerProps || {})} className="overflow-hidden">
          <div ref={ref} className={cn('w-full', className, { 'aiui-dark': theme === 'dark' })}>
            {title && <Title data={{ title: title }} />}
            <SwiperBox
              className="!overflow-visible"
              id={'Evaluate' + key}
              data={{ list: products, configuration: { ...other, title: title, locale } }}
              Slide={EvaluateItem}
              breakpoints={{
                0: {
                  spaceBetween: 12,
                  freeMode: false,
                  slidesPerView: 1,
                },
                374: {
                  spaceBetween: 12,
                  freeMode: false,
                  slidesPerView: 1.2,
                },
                768: {
                  spaceBetween: 16,
                  freeMode: false,
                  slidesPerView: 2.3,
                },
                1024: {
                  spaceBetween: 16,
                  freeMode: false,
                  slidesPerView: 3,
                },
                1440: {
                  spaceBetween: 16,
                  freeMode: false,
                  slidesPerView: 4,
                },
              }}
            />
          </div>
        </Container>
      </div>
    </div>
  )
})

Evaluate.displayName = 'Evaluate'

export default withLayout(Evaluate)
